import type { SubscriptionTier } from '../../config/subscriptionPlans';

export interface EconomicBillingStatus {
  mode: 'economic';
  epochId: string;
  tier: SubscriptionTier;
  remainingPercent: number | null;
  cycleReference: string;
  cycleEndAtMs: number | null;
  credits: { availableEur: string; promotionalAvailableEur?: string; restrictedEur?: string; activation: 'inactive' | 'active' | 'empty'; activationId?: string | null; noticeConsumed?: boolean };
  canStartExecution: boolean;
  scheduledChange?: { label: 'Cambia' | 'Finaliza'; effectiveAtMs: number; targetTier: SubscriptionTier; targetName: string } | null;
  fiscalState: 'ready' | 'unavailable';
  checkoutEnabled: boolean;
  billingStatus?: 'active' | 'paused';
  contracts?: { requestId: string; createdAtMs: number; confirmed: boolean }[];
}

export type PurchaseTarget = { kind: 'credits'; amountCents: 500 | 1000 | 2000 }
  | { kind: 'subscription'; tier: 'standard' | 'professional' | 'premium' };
export interface PurchaseResidence { country: 'ES'; postalCode: string; consumer: true }
export interface PurchaseQuote {
  quoteId: string; quoteDigest: string; requestId: string; target: PurchaseTarget; validUntilMs: number;
  offer: { usefulCents: number; subtotalCents: number; taxCents: number; totalCents: number; taxLabel: string; startsNewCycle: boolean };
  terms: { version: string; url: string; text: string }; privacy: { version: string; url: string; text: string };
  immediateStartText: string; withdrawalText: string;
}
export interface PendingPurchase { target: PurchaseTarget; requestId: string; quote: PurchaseQuote | null }
export function safeStripeInvoiceUrl(value: unknown) {
  if (typeof value !== 'string') return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname === 'invoice.stripe.com' && !url.port && !url.username && !url.password
      && url.pathname.startsWith('/i/') && url.pathname.length > 3 && !url.hash ? url.href : null;
  } catch { return null; }
}
export const isPaidUpgrade = (current: SubscriptionTier, target: SubscriptionTier) =>
  ['free', 'standard', 'professional', 'premium', 'admin'].indexOf(target)
    > ['free', 'standard', 'professional', 'premium', 'admin'].indexOf(current) && target !== 'admin';

export const formatCreditEur = (amount: string) => {
  const value = Number(amount);
  return value > 0 && value < 0.01 ? '<0,01 €' : value.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
};

export function economicSendingDisabled(status: EconomicBillingStatus | null, previewActive: boolean, recoveryRequired = false) {
  return Boolean(!previewActive && status?.tier !== 'admin' && (recoveryRequired || status && !status.canStartExecution));
}

export function activationKey(status: EconomicBillingStatus) {
  return `${status.epochId}:${status.cycleReference}:${status.credits.activationId ?? ''}`;
}

export const creditNoticeStorageKey = (uid: string) => `juris.economic-credit-notice.v1:${uid}`;
export const isEconomicAdmissionError = (error: unknown) => {
  const kind = (error as { errorKind?: string } | null)?.errorKind;
  return kind === 'economic_capacity_required' || kind === 'economic_billing_unavailable';
};
